import { useMemo } from 'react'
import cn from 'classnames'
import {
   DeltaBadge,
   StatTile,
} from '@forwardflow/features/campaign-flow/components/stats/StatParts'
import {
   IMetric,
   IResults,
   TPeriod,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import {
   PERIOD_LABEL,
   compact,
   delta,
   percent,
   rate,
   trendLabel,
} from '@forwardflow/features/campaign-flow/utils/format'

const PERIODS: TPeriod[] = ['7d', '30d', '90d', 'all']

/** 7 days · 30 days · 90 days · All time. */
export const PeriodSwitch = ({
   value,
   onChange,
   testId = 'cf-period',
}: {
   value: TPeriod
   onChange: (p: TPeriod) => void
   testId?: string
}) => (
   <div
      className="flex gap-0.5 bg-white/[0.05] rounded-full p-0.5"
      role="tablist"
      data-testid={testId}
   >
      {PERIODS.map((p) => (
         <button
            key={p}
            type="button"
            role="tab"
            aria-selected={value === p}
            onClick={() => onChange(p)}
            className={cn(
               'h-8 px-3.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors',
               value === p
                  ? 'bg-white/[0.12] text-white'
                  : 'text-[#9ca3af] hover:text-white'
            )}
            data-testid={`${testId}-${p}`}
         >
            {PERIOD_LABEL[p]}
         </button>
      ))}
   </div>
)

/** A secondary number: label, value, change. */
const SmallStat = ({
   label,
   m,
   format,
   lowerIsBetter,
   hint,
   testId,
}: {
   label: string
   m: IMetric
   format: (v: number) => string
   lowerIsBetter?: boolean
   hint: string
   testId?: string
}) => (
   <div
      className="flex items-center gap-3 px-4 py-3 min-w-0"
      title={hint}
      data-testid={testId}
   >
      <div className="min-w-0">
         <div className="text-xs text-[#9ca3af] truncate">{label}</div>
         <div className="flex items-center gap-2 mt-0.5">
            <span className="text-lg font-semibold">{format(m.value)}</span>
            <DeltaBadge d={delta(m, lowerIsBetter)} vs="" />
         </div>
      </div>
   </div>
)

/**
 * The results, shown the same way for the whole organization and for one
 * campaign: four numbers with their change and trend, then how the calls and
 * texts are landing.
 */
export const ResultsBlock = ({
   stats,
   leadsFootnote,
   testId = 'cf-overview',
   openActivity,
}: {
   stats: IResults
   /** Under "Leads worked": what the leads are counted against. */
   leadsFootnote?: string
   testId?: string
   /** Makes the activity tiles links into Conversations with the matching filter. */
   openActivity?: (kind: 'reply' | 'appointment' | 'transfer') => void
}) => {
   const labels = useMemo(
      () => Array.from({ length: 12 }, (_, i) => trendLabel(stats.period, i)),
      [stats.period]
   )
   return (
      <section className="flex flex-col gap-3" data-testid={testId}>
         <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <StatTile
               testId="cf-kpi-leads"
               icon="users"
               label="Leads worked"
               value={compact(stats.leads.value)}
               d={delta(stats.leads)}
               trend={stats.leads.trend}
               labels={labels}
               footnote={leadsFootnote ?? 'Leads we called or texted'}
            />
            <StatTile
               testId="cf-kpi-conversations"
               icon="chat"
               onOpen={openActivity && (() => openActivity('reply'))}
               label="Conversations"
               value={compact(stats.conversations.value)}
               d={delta(stats.conversations)}
               trend={stats.conversations.trend}
               labels={labels}
               footnote={
                  <>
                     <b className="text-white font-semibold">
                        {percent(
                           rate(stats.conversations.value, stats.leads.value)
                        )}
                     </b>{' '}
                     of leads talked back
                  </>
               }
            />
            <StatTile
               testId="cf-kpi-appointments"
               icon="cal"
               onOpen={openActivity && (() => openActivity('appointment'))}
               label="Appointments"
               value={compact(stats.appointments.value)}
               d={delta(stats.appointments)}
               trend={stats.appointments.trend}
               labels={labels}
               footnote={
                  <>
                     <b className="text-white font-semibold">
                        {percent(
                           rate(stats.appointments.value, stats.leads.value)
                        )}
                     </b>{' '}
                     of leads booked
                  </>
               }
            />
            <StatTile
               testId="cf-kpi-transfers"
               icon="phone"
               onOpen={openActivity && (() => openActivity('transfer'))}
               label="Live transfers"
               value={compact(stats.transfers.value)}
               d={delta(stats.transfers)}
               trend={stats.transfers.trend}
               labels={labels}
               footnote={
                  <>
                     <b className="text-white font-semibold">
                        {percent(
                           rate(
                              stats.transfers.value,
                              stats.conversations.value
                           )
                        )}
                     </b>{' '}
                     of conversations sent to your team
                  </>
               }
            />
         </div>
         <div
            className="grid grid-cols-1 sm:grid-cols-3 rounded-[14px] bg-white/[0.03] border border-white/[0.06] divide-y sm:divide-y-0 sm:divide-x divide-white/[0.06]"
            data-testid="cf-speed"
         >
            <SmallStat
               label="Calls and texts per lead"
               m={stats.attemptsPerLead}
               format={(v) => v.toFixed(1)}
               lowerIsBetter
               hint="How many tries it takes before a lead answers. Lower is better."
               testId="cf-stat-attempts"
            />
            <SmallStat
               label="Calls answered"
               m={stats.callsAnswered}
               format={(v) => percent(v)}
               hint="Share of calls a lead picked up."
               testId="cf-stat-answered"
            />
            <SmallStat
               label="Texts replied to"
               m={stats.textsReplied}
               format={(v) => percent(v)}
               hint="Share of texts that got a reply."
               testId="cf-stat-replied"
            />
         </div>
      </section>
   )
}
